import React, {useEffect, useState} from 'react';
import {useQuery, useMutation} from '@apollo/client';
import {DELETE_LANDLORD, GET_LANDLORDS} from "../graphql/landlords";
import {ListComponent} from "../components/ListComponent";
import {FilterBar} from "../components/FilterBar";
import people from "../assets/images/people-black.svg";
import {CardComponent} from "../components/CardComponent";
import {ListRowComponent} from "../components/ListRowComponent";
import {PersonCardContentComponent} from "../components/PersonCardContentComponent";
import {useNavigate} from 'react-router-dom';

const Landlords = () => {
    const navigate = useNavigate();

    const [searchTerm, setSearchTerm] = useState('');
    const [landlords, setLandlords] = useState({});
    const [sortBy, setSortBy] = useState('surname');
    const [viewMode, setViewMode] = useState('grid');
    const [pageIndex, setPageIndex] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [activePill, setActivePill] = useState('all');
    const [propertyCountBuckets, setPropertyCountBuckets] = useState([]);

    const buildFilter = () => {
        const f = {
            isActive: true,
            isActiveLandlord: true,
        };

        if (searchTerm) {
            f.or = [
                { firstName: { regex: searchTerm } },
                { lastName: { regex: searchTerm } },
                { email: { regex: searchTerm } },
            ];
        }

        if (activePill === 'ficaComplete') f.hasFicaDocument = true;
        else if (activePill === 'ficaPending') f.hasFicaDocument = { ne: true };

        return f;
    };

    const query = useQuery(GET_LANDLORDS,{
        variables: {
            pageIndex,
            pageSize,
            orderAsc: true,
            orderField: "surname",
            filter: { isActive: true },
            propertyCountBuckets: []
        },
        onCompleted: (data) => {
            setLandlords(data?.landlords);
        }
    });

    useEffect(() => {
        const fetchData = async () => {
            const {data} = await query.refetch({
                pageIndex,
                pageSize,
                orderAsc: true,
                orderField: sortBy,
                filter: buildFilter(),
                propertyCountBuckets
            });
            setLandlords(data?.landlords);
        };
        const debounceTimer = setTimeout(fetchData, 300);
        return () => clearTimeout(debounceTimer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchTerm, sortBy, pageIndex, pageSize, activePill, propertyCountBuckets]);

    const sortOptions = [
        {key: "surname", value: "Sort by Surname"},
        {key: "firstName", value: "Sort by First Name"},
        {key: "properties", value: "Sort by Properties Count"}
    ];

    const [deleteLandlord] = useMutation(DELETE_LANDLORD, {
        refetchQueries: [{query: GET_LANDLORDS}],
        onCompleted: () => console.log('Landlord deleted successfully'),
        onError: (error) => {
            console.error('Error deleting landlord:', error);
            alert('Failed to delete landlord. Please try again.');
        }
    });

    const handleDelete = (id, name) => {
        if (window.confirm(`Are you sure you want to delete ${name}? This action cannot be undone.`)) {
            deleteLandlord({variables: {id}});
        }
    };

    const handleDetails = (id) => navigate(`/landlords/${id}`);
    const handleEdit = (id) => navigate(`/landlords/edit/${id}`);

    if (query.loading && !landlords?.items) return (
        <div className="flex justify-center items-center h-64">
            <div className="spinner-border animate-spin inline-block w-8 h-8 border-4 rounded-full text-blue-600" role="status">
                <span className="visually-hidden">Loading...</span>
            </div>
        </div>
    );

    if (query.error) return (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
            <strong className="font-bold">Error!</strong>
            <span className="block sm:inline"> {query.error.message}</span>
        </div>
    );

    const resetFilters = () => {
        setSearchTerm('');
        setSortBy('surname');
        setActivePill('all');
        setPropertyCountBuckets([]);
        setPageIndex(0);
    };

    const handleViewModeChange = (mode) => setViewMode(mode);
    const handlePageSizeChange = (size) => { setPageSize(size); setPageIndex(0); };
    const handlePillChange = (key) => { setActivePill(key); setPageIndex(0); };
    const handleMultiSelectChange = (key, values) => {
        if (key === 'propertyCount') setPropertyCountBuckets(values);
        setPageIndex(0);
    };

    const filterBar = (
        <FilterBar
            pills={[
                { key: 'all', label: 'All' },
                { key: 'ficaComplete', label: 'FICA Complete' },
                { key: 'ficaPending', label: 'FICA Pending' },
            ]}
            activePill={activePill}
            onPillChange={handlePillChange}
            multiSelects={[
                {
                    key: 'propertyCount',
                    label: 'Properties',
                    options: [
                        { value: '0',    label: 'No properties' },
                        { value: '1',    label: '1 property' },
                        { value: '2-5',  label: '2 – 5 properties' },
                        { value: '6-10', label: '6 – 10 properties' },
                        { value: '10+',  label: '10+ properties' },
                    ],
                    values: propertyCountBuckets,
                },
            ]}
            onMultiSelectChange={handleMultiSelectChange}
            onClearAll={() => { setActivePill('all'); setPropertyCountBuckets([]); setPageIndex(0); }}
        />
    );

    return (<ListComponent
        title="Landlords"
        headerIcon={people}
        addTitle="Landlord"
        addLink="/landlords/add"
        searchTerm={searchTerm}
        searchTermCallback={setSearchTerm}
        resetFiltersCallback={resetFilters}
        sortCallback={setSortBy}
        defaultSortOption={sortBy}
        sortOptions={sortOptions}
        viewMode={viewMode}
        viewModeCallback={handleViewModeChange}
        filterBar={filterBar}
        pageInfo={{
            totalCount: landlords?.pageInfo?.totalCount,
            totalPages: landlords?.pageInfo?.totalPages,
            pageSize: landlords?.pageInfo?.pageSize,
            pageIndex: landlords?.pageInfo?.pageIndex
        }}
        pageCallback={setPageIndex}
        pageSizeCallback={handlePageSizeChange}>

        {landlords?.items?.map(landlord => {
            const componentProps = {
                cardId: landlord.id,
                deleteCallback: () => handleDelete(landlord.id, `${landlord.firstName} ${landlord.lastName}`),
                editCallback: handleEdit,
                detailsCallback: handleDetails,
                headerImageText: `${landlord.firstName.charAt(0)}${landlord.lastName.charAt(0)}`,
                headerTitle: `${landlord.firstName} ${landlord.lastName}`,
                children: <PersonCardContentComponent
                    email={landlord.email}
                    phone={landlord.primaryPhone}
                    secondaryPhone={landlord.secondaryPhone}
                    properties={landlord.properties}
                />
            };

            return viewMode === 'list' ? (
                <ListRowComponent key={landlord.id} {...componentProps} />
            ) : (
                <CardComponent key={landlord.id} {...componentProps} />
            );
        })}
    </ListComponent>);
};

export default Landlords;
